import { create } from 'zustand';

/**
 * Launcher V2 C3 (plan `2026-09-01-001`) — the Sequences step's copy-preview
 * state: which campaign member the rendered copy interpolates for, and whether
 * the sequence editor is showing its editors (`'edit'`) or that rendered copy
 * (`'preview'`).
 *
 * Both fields were component state on `SequenceEditorShell` until C3. They had
 * to move, and the reason is structural rather than stylistic: the click that
 * picks a preview member now comes from a row of the shared `MembersPanel`
 * table, which `PlayFlowShell` builds once and hands to each step as a
 * `membersPanel` slot. `MembersPanel` and `SequenceEditorShell` are therefore
 * SIBLINGS with no common ancestor that holds this state — threading it as
 * props would mean `PlayFlowShell` subscribing to sequence-editor state it has
 * no other use for, and would leave the wiring reachable only through the
 * shell. Exactly the situation `rung-scope-store.ts`'s own module doc describes
 * for the rung scope and the Audience Rule draft, resolved the same way: a
 * module-scope zustand store (root CLAUDE.md "Zustand for Module-Scope Client
 * State"), which additionally survives the Glaze `TabPanel` unmount every step
 * switch performs.
 *
 * Deliberately NOT keyed by rung. One mode governs the sequence editor at a
 * time (matching the prototype's single `state.mode`), and a member id that
 * doesn't belong to the newly-selected rung's roster simply falls back to that
 * rung's own sample member at the call site (`SequencesStep`'s `previewMember`),
 * so a stale id can't show one rung's copy under another rung's member.
 *
 * `PlayFlowShell` resets this store to {@link initialState} on mount and
 * unmount, keyed on `playId`, alongside the other play-flow stores.
 */

export type SequenceEditorMode = 'edit' | 'preview';

interface SequencePreviewState {
  /**
   * The member the rep explicitly picked from `MembersPanel`, or `null` for
   * "no explicit pick yet" — which is NOT the same as "no member to preview":
   * `SequencesStep` falls back to its own `selectSampleMember` result so
   * Preview mode has something real to render the first time it's opened.
   */
  selectedMemberId: string | null;
  /** `'edit'` is the prototype's documented default (`playbooks-prototype-v6.html:2912`). */
  editorMode: SequenceEditorMode;
}

/** Exported per the store convention — reset idiom is `useSequencePreviewStore.setState(initialState, true)`. */
export const initialState: SequencePreviewState = {
  selectedMemberId: null,
  editorMode: 'edit',
};

export const useSequencePreviewStore = create<SequencePreviewState>(() => ({ ...initialState }));

/**
 * A member-row click: sets who the preview is for AND opens Preview mode.
 *
 * The mode flip is part of the same action on purpose — the prototype's own
 * `'select-member'` handler (`playbooks-prototype-v6.html:8369-8372`) does
 * both, so that the first click on a row visibly does something instead of
 * silently staging a member behind an editor the rep is still looking at.
 */
export function selectPreviewMember(memberId: string): void {
  useSequencePreviewStore.setState({ selectedMemberId: memberId, editorMode: 'preview' });
}

export function setSequenceEditorMode(mode: SequenceEditorMode): void {
  useSequencePreviewStore.setState({ editorMode: mode });
}

/** Resets both fields — the Play-scoped reset `PlayFlowShell` performs. */
export function clearSequencePreview(): void {
  useSequencePreviewStore.setState({ ...initialState });
}

export function selectPreviewMemberId(state: SequencePreviewState): string | null {
  return state.selectedMemberId;
}

export function selectSequenceEditorMode(state: SequencePreviewState): SequenceEditorMode {
  return state.editorMode;
}
